import PropTypes from "prop-types";
import * as React from "react";
import { Link } from "react-router-dom";
import { useCurrentUser } from "../globalState";
import { useShopSwitcher } from "../hooks";
import { useNotification } from "../services/Notifications";
import {
  AddIcon,
  Avatar,
  Button,
  IconButton,
  List,
  PushPinFilledIcon,
  PushPinIcon,
  SearchInput,
  Spinner,
  Typography,
} from "../themeV2";
import { getInitials } from "../util/text";
import styles from "./ShopSwitcherPopover.module.css";

const KIND_LABEL = {
  talent: "Talent",
  collection: "Collection",
  agent: "Agent",
  talentCoordinator: "Talent Coordinator",
};

const shopShape = PropTypes.shape({
  influencerId: PropTypes.number.isRequired,
  name: PropTypes.string,
  route: PropTypes.string.isRequired,
  avatarUrl: PropTypes.string,
  kind: PropTypes.oneOf(Object.keys(KIND_LABEL)).isRequired,
  pinned: PropTypes.bool,
});

ShopRow.propTypes = {
  shop: shopShape.isRequired,
  to: PropTypes.string.isRequired,
  isCurrent: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onTogglePin: PropTypes.func.isRequired,
};

function ShopRow({ shop, to, isCurrent, onOpen, onTogglePin }) {
  return (
    <List.ItemButton
      as={Link}
      to={to}
      onClick={onOpen}
      aria-current={isCurrent ? "page" : undefined}
      className={isCurrent ? styles.shopOptionCurrent : styles.shopOption}
      leading={
        <Avatar
          src={shop.avatarUrl}
          alt=""
          size="sm"
          className={styles.shopAvatar}
        >
          {getInitials(shop.name)}
        </Avatar>
      }
      secondary={
        <Typography variant="label-lg" color="variant" truncate>
          {KIND_LABEL[shop.kind]}
        </Typography>
      }
      action={
        <IconButton
          label={`Pin ${shop.name}`}
          toggle
          pressed={Boolean(shop.pinned)}
          size="sm"
          onClick={() => onTogglePin(shop)}
        >
          {shop.pinned ? <PushPinFilledIcon /> : <PushPinIcon />}
        </IconButton>
      }
    >
      <Typography
        variant="body-lg"
        truncate
        className={isCurrent ? styles.currentName : styles.shopName}
      >
        {shop.name}
      </Typography>
    </List.ItemButton>
  );
}

ShopSection.propTypes = {
  title: PropTypes.string,
  shops: PropTypes.arrayOf(shopShape).isRequired,
  rowProps: PropTypes.func.isRequired,
};

function ShopSection({ title, shops, rowProps }) {
  if (shops.length === 0) return null;

  return (
    <>
      {title && <List.Subheader>{title}</List.Subheader>}
      {shops.map((shop) => (
        <ShopRow key={shop.influencerId} shop={shop} {...rowProps(shop)} />
      ))}
    </>
  );
}

ShopSwitcherPopover.displayName = "ShopSwitcherPopover";
ShopSwitcherPopover.propTypes = {
  onClose: PropTypes.func.isRequired,
  onAddShop: PropTypes.func.isRequired,
};

export default function ShopSwitcherPopover({ onClose, onAddShop }) {
  const currentUser = useCurrentUser();
  const notify = useNotification();
  const {
    activeInfluencer,
    searchQuery,
    setSearchQuery,
    isSearching,
    pinned,
    recent,
    results,
    isLoading,
    pathFor,
    setPinned,
  } = useShopSwitcher();

  const handleOpen = React.useCallback(() => {
    setSearchQuery("");
    onClose();
  }, [onClose, setSearchQuery]);

  const handleTogglePin = React.useCallback(
    (shop) =>
      setPinned(shop, !shop.pinned).catch(() =>
        notify({ message: `Couldn't update the pin for ${shop.name}` }),
      ),
    [notify, setPinned],
  );

  const rowProps = (shop) => ({
    to: pathFor(shop),
    isCurrent: activeInfluencer?.influencerId === shop.influencerId,
    onOpen: handleOpen,
    onTogglePin: handleTogglePin,
  });

  const noResults = isSearching && !isLoading && results.length === 0;

  return (
    <>
      <div className={styles.searchWrap}>
        <SearchInput
          value={searchQuery}
          onValueChange={setSearchQuery}
          placeholder="Shop name or link"
          label="Search shops"
          autoFocus
        />
      </div>

      {isLoading ? (
        <div className={styles.status}>
          <Spinner label="Loading shops" />
        </div>
      ) : (
        <List.Root className={styles.shopList} aria-label="Shops">
          {isSearching ? (
            <ShopSection shops={results} rowProps={rowProps} />
          ) : (
            <>
              <ShopSection title="Pinned" shops={pinned} rowProps={rowProps} />
              <ShopSection title="Recent" shops={recent} rowProps={rowProps} />
            </>
          )}
        </List.Root>
      )}

      <p
        role="status"
        className={noResults ? styles.noResults : styles.visuallyHidden}
      >
        {noResults ? `No shops match “${searchQuery}”.` : ""}
      </p>

      {currentUser.isAdmin && !activeInfluencer?.isTcShop && (
        <div className={styles.addWrap}>
          <Button
            variant="tonal"
            fullWidth
            leading={<AddIcon />}
            onClick={() => {
              onClose();
              onAddShop();
            }}
          >
            Add shop
          </Button>
        </div>
      )}
    </>
  );
}
